<template>
  <div class="invoice-upload-content">
    <!-- 月份选择 -->
    <div class="month-selector">
      <el-form :inline="true">
        <el-form-item label="账单月份:">
          <el-date-picker
            v-model="selectedMonth"
            type="month"
            placeholder="选择月份"
            format="yyyy-MM"
            value-format="yyyyMM"
            :clearable="false"
          />
        </el-form-item>
        <el-form-item>
          <el-tag type="info" size="small">
            当前选择: {{ selectedMonth ? formatMonth(selectedMonth) : '未选择' }}
          </el-tag>
        </el-form-item>
      </el-form>
    </div>

    <!-- 文件上传区域 -->
    <el-upload
      ref="upload"
      class="upload-demo"
      drag
      action="#"
      :auto-upload="false"
      :on-change="handleFileChange"
      :on-remove="handleFileRemove"
      :file-list="fileList"
      :limit="20"
      :on-exceed="handleExceed"
      accept=".pdf"
      multiple
    >
      <i class="el-icon-upload"></i>
      <div class="el-upload__text">将PDF文件拖到此处，或<em>点击选择文件</em></div>
      <div class="el-upload__tip" slot="tip">
        支持批量上传，一次最多上传20个PDF文件，单个文件不超过10MB
      </div>
    </el-upload>

    <!-- 文件列表预览 -->
    <div v-if="fileList.length > 0" class="file-list-preview">
      <el-divider content-position="left">待上传文件列表 ({{ fileList.length }})</el-divider>
      <el-table :data="fileList" style="width: 100%">
        <el-table-column prop="name" label="文件名" min-width="200">
          <template slot-scope="scope">
            <i class="el-icon-document"></i> {{ scope.row.name }}
          </template>
        </el-table-column>
        <el-table-column label="文件大小" width="120">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.size) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag size="small" type="info">待上传</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="removeFile(scope.$index)">
              移除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 上传按钮 -->
    <div class="upload-actions" v-if="fileList.length > 0">
      <el-button
        type="primary"
        @click="submitUpload"
        :loading="uploading"
        icon="el-icon-upload"
      >
        开始上传并识别 ({{ fileList.length }}个文件)
      </el-button>
      <el-button @click="clearAll">取消</el-button>
    </div>

    <!-- 上传进度 -->
    <div v-if="uploading" class="upload-progress">
      <el-progress
        :percentage="uploadProgress"
        :status="uploadProgress === 100 ? 'success' : ''"
      ></el-progress>
      <p class="progress-text">正在处理文件，请稍候...</p>
    </div>

    <!-- 上传结果 -->
    <div v-if="uploadResults.length > 0" class="upload-results">
      <el-divider content-position="left">
        上传结果 (成功: {{ successCount }}, 失败: {{ failCount }})
      </el-divider>

      <el-table :data="uploadResults" style="width: 100%">
        <el-table-column prop="file_name" label="文件名" min-width="200" />

        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.status === 'success' ? 'success' : 'danger'"
              size="small"
            >
              {{ scope.row.status === 'success' ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="invoice_id" label="Invoice ID" width="150" />

        <el-table-column label="错误信息" min-width="200">
          <template slot-scope="scope">
            <span v-if="scope.row.error" style="color: red">{{ scope.row.error }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="result-actions">
        <el-button @click="clearResults">清空结果</el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InvoiceUploadContent',
  data() {
    const lastMonth = new Date()
    lastMonth.setMonth(lastMonth.getMonth() - 1)
    const defaultMonth = lastMonth.getFullYear() + String(lastMonth.getMonth() + 1).padStart(2, '0')

    return {
      fileList: [],
      uploading: false,
      uploadProgress: 0,
      uploadResults: [],
      selectedMonth: defaultMonth
    }
  },
  computed: {
    successCount() {
      return this.uploadResults.filter(r => r.status === 'success').length
    },
    failCount() {
      return this.uploadResults.filter(r => r.status === 'failed').length
    }
  },
  methods: {
    handleFileChange(file, fileList) {
      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        this.$message.error(`文件 ${file.name} 超过10MB限制`)
        this.fileList = fileList.filter(f => f.uid !== file.uid)
        return
      }

      if (!file.name.toLowerCase().endsWith('.pdf')) {
        this.$message.error(`文件 ${file.name} 不是PDF格式`)
        this.fileList = fileList.filter(f => f.uid !== file.uid)
        return
      }

      this.fileList = fileList
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList
    },

    handleExceed(files, fileList) {
      this.$message.warning(
        `最多只能上传20个文件，当前已选择${fileList.length}个文件，本次选择了${files.length}个文件`
      )
    },

    removeFile(index) {
      this.fileList.splice(index, 1)
      this.$refs.upload.clearFiles()
      this.fileList.forEach(file => {
        this.$refs.upload.handleStart(file.raw)
      })
    },

    clearAll() {
      this.fileList = []
      this.$refs.upload.clearFiles()
    },

    async submitUpload() {
      if (this.fileList.length === 0) {
        this.$message.warning('请先选择文件')
        return
      }

      if (!this.selectedMonth) {
        this.$message.warning('请选择账单月份')
        return
      }

      const formData = new FormData()
      this.fileList.forEach(file => {
        formData.append('files', file.raw)
      })
      formData.append('month', this.selectedMonth)

      this.uploading = true
      this.uploadProgress = 0
      this.uploadResults = []

      const progressInterval = setInterval(() => {
        if (this.uploadProgress < 90) {
          this.uploadProgress += 10
        }
      }, 500)

      try {
        const response = await this.$http.post('/bill/invoice/upload/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        clearInterval(progressInterval)
        this.uploadProgress = 100

        if (response.data.status === 'success' || response.data.status === 'partial') {
          this.$message.success(response.data.message)
          this.uploadResults = response.data.results

          this.fileList = []
          this.$refs.upload.clearFiles()

          this.$emit('success')
        } else {
          this.$message.error(response.data.message)
        }
      } catch (error) {
        clearInterval(progressInterval)
        this.$message.error('上传失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.uploading = false
        setTimeout(() => {
          this.uploadProgress = 0
        }, 2000)
      }
    },

    clearResults() {
      this.uploadResults = []
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    },

    formatMonth(month) {
      if (!month || month.length !== 6) return month
      return month.substring(0, 4) + '年' + month.substring(4, 6) + '月'
    }
  }
}
</script>

<style scoped>
.invoice-upload-content {
  padding: 10px;
}

.month-selector {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.upload-demo {
  margin-bottom: 20px;
}

.file-list-preview {
  margin-top: 20px;
}

.upload-actions {
  margin-top: 20px;
  text-align: center;
}

.upload-progress {
  margin-top: 20px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.progress-text {
  text-align: center;
  margin-top: 10px;
  color: #606266;
}

.upload-results {
  margin-top: 30px;
}

.result-actions {
  margin-top: 20px;
  text-align: center;
}
</style>
